import { FactoryOpts, MaskedEnum } from 'imask';
import {
  Country,
  austria,
  belgium,
  brazil,
  bulgaria,
  croatia,
  cyprus,
  czechRepublic,
  denmark,
  estonia,
  finland,
  france,
  germany,
  greece,
  hungary,
  ireland,
  italy,
  latvia,
  lithuania,
  luxembourg,
  malta,
  netherlands,
  norway,
  poland,
  portugal,
  romania,
  slovakiaRepublic,
  slovenia,
  spain,
  sweden,
  switzerland,
  unitedKingdom,
} from 'jsvat';
import { CountryCode } from 'libphonenumber-js';

/**
 * This file is containing tax id and vat eu masks
 */

export const vatFieldTypes = [
  'AT_EU',
  'AT_VAT',
  'BE_EU',
  'BE_VAT',
  'BG_EU',
  'BR_VAT',
  'CH_VAT',
  'CY_EU',
  'CY_VAT',
  'CZ_EU',
  'DE_EU',
  'DK_EU',
  'EE_EU',
  'EE_VAT',
  'GR_EU',
  'ES_EU',
  'FI_EU',
  'FI_VAT',
  'FR_EU',
  'HU_EU',
  'HR_EU',
  'IE_EU',
  'IT_EU',
  'LT_EU',
  'LU_EU',
  'LV_EU',
  'MT_EU',
  'NL_EU',
  'NO_VAT',
  'PL_EU',
  'PL_VAT',
  'PT_EU',
  'RO_EU',
  'SK_EU',
  'SI_EU',
  'SE_EU',
  'SE_VAT',
  'GB_VAT',
  'other',
] as const;

export type VatFieldType = (typeof vatFieldTypes)[number];

export const appendPrefix = (vatCode: string, vatNumber: string) => {
  const [country, suffix] = vatCode.split('_');

  // Czech republic has universal prefix
  if (suffix === 'VAT' && !['CZ', 'GB'].includes(country)) {
    return `${country}${vatNumber}`;
  } else {
    return vatNumber;
  }
};

export const getVatValidationObject = (countryCode: string) => {
  const VAT_CODE_VALIDATION: { [key: string]: Country } = {
    AT_EU: austria,
    AT_VAT: austria,
    BE_EU: belgium,
    BG_EU: bulgaria,
    BR_VAT: brazil,
    CH_VAT: switzerland,
    CY_EU: cyprus,
    CY_VAT: cyprus,
    CZ_EU: czechRepublic,
    DE_EU: germany,
    DK_EU: denmark,
    EE_EU: estonia,
    EE_VAT: estonia,
    GR_EU: greece,
    ES_EU: spain,
    FI_EU: finland,
    FI_VAT: finland,
    FR_EU: france,
    HU_EU: hungary,
    HR_EU: croatia,
    IE_EU: ireland,
    IT_EU: italy,
    LT_EU: lithuania,
    LU_EU: luxembourg,
    LV_EU: latvia,
    MT_EU: malta,
    NL_EU: netherlands,
    NO_VAT: norway,
    PL_EU: poland,
    PL_VAT: poland,
    PT_EU: portugal,
    RO_EU: romania,
    SK_EU: slovakiaRepublic,
    SI_EU: slovenia,
    SE_EU: sweden,
    SE_VAT: sweden,
    GB_VAT: unitedKingdom,
  };

  return VAT_CODE_VALIDATION[countryCode] || undefined;
};

export const mapCountryCodeToVatType = (countryCode: CountryCode) => {
  return [`${countryCode}_EU`, `${countryCode}_VAT`];
};

export const getVatFieldConfig = (vatCode: string) => {
  const VAT_FIELD_CONFIG: {
    [key: string]: {
      inputType: 'text' | 'tel';
    } & FactoryOpts;
  } = {
    // AT: eu/domestic differs only in prefix
    AT_EU: {
      mask: '{ATU}00000000',
      inputType: 'tel',
    },
    AT_VAT: {
      mask: '{U}00000000',
      inputType: 'tel',
    },
    BE_EU: {
      mask: '{BE}\\0000000000',
      inputType: 'tel',
    },
    BG_EU: {
      mask: '{BG}0000000000',
      inputType: 'tel',
    },
    BR_VAT: {
      mask: '00.000.000/000-00',
      inputType: 'tel',
    },
    CH_VAT: {
      mask: '{CHE-}000.000.000 XX',
      blocks: {
        XX: {
          mask: MaskedEnum,
          /**
           * FIXME: https://github.com/uNmAnNeR/imaskjs/issues/793
           */
          //@ts-ignore
          enum: ['MWST', 'TVA', 'IVA'],
        },
      },
      inputType: 'text',
    },
    CY_EU: {
      mask: '00000000a',
      inputType: 'text',
    },
    CY_VAT: {
      mask: '{CY}00000000a',
      inputType: 'text',
    },
    // CZ: country and eu numbers are the same - prefix + 8-10 numbers
    CZ_EU: {
      mask: '{CZ}0000000000',
      inputType: 'tel',
    },
    DK_EU: {
      mask: '{DK}00000000',
      inputType: 'tel',
    },
    // omitting germany's domestic for now, too intricate
    DE_EU: {
      mask: '{DE}000000000',
      inputType: 'tel',
    },
    EE_EU: {
      mask: '{EE}000000000',
      inputType: 'tel',
    },
    EE_VAT: {
      mask: '000000000',
      inputType: 'tel',
    },
    GR_EU: {
      mask: '{EL}000000000',
      inputType: 'tel',
    },
    ES_EU: {
      mask: [{ mask: '{ES}a00000000' }, { mask: '{ES}a0000000a' }, { mask: '{ES}00000000a' }],
      inputType: 'text',
    },
    FI_EU: {
      mask: '{FI}00000000',
      inputType: 'tel',
    },
    FI_VAT: {
      mask: '0000000-0',
      inputType: 'tel',
    },
    FR_EU: {
      mask: [
        {
          mask: '{FR}aa000000000',
        },
        {
          mask: '{FR}a0000000000',
        },
        {
          mask: '{FR}0a000000000',
        },
        {
          mask: '{FR}00000000000',
        },
      ],
      inputType: 'text',
    },
    HR_EU: {
      mask: '{HR}00000000000',
      inputType: 'tel',
    },
    HU_EU: {
      mask: '{HU}00000000',
      inputType: 'tel',
    },
    IE_EU: {
      mask: '{IE}0000000aa',
      inputType: 'text',
    },
    IT_EU: {
      mask: '{IT}00000000000',
      inputType: 'tel',
    },
    LT_EU: {
      mask: [{ mask: '{LT}000000000' }, { mask: '{LT}000000000000' }],
      inputType: 'tel',
    },
    LU_EU: {
      mask: '{LU}00000000',
      inputType: 'tel',
    },
    LV_EU: {
      mask: '{LV}00000000000',
      inputType: 'tel',
    },
    MT_EU: {
      mask: '{MT}00000000',
      inputType: 'tel',
    },
    NL_EU: {
      mask: '{NL}000000000B00',
      inputType: 'tel',
    },
    // TODO: react-input-mask compatibility
    NO_VAT: {
      mask: '{NO} 000 000 000[ ][MVA]',
      inputType: 'text',
    },
    // PL: eu/domestic numbers differ only in prefix
    PL_EU: {
      mask: '{PL}0000000000',
      inputType: 'tel',
    },
    PL_VAT: { mask: '0000000000', inputType: 'tel' },
    PT_EU: { mask: '{PT5}00000000', inputType: 'tel' },
    // Romania - 2-10 digits are valid
    RO_EU: { mask: '{RO}0000000000', inputType: 'tel' },
    SK_EU: { mask: '{SK}0000000000', inputType: 'tel' },
    SI_EU: { mask: '{SI}00000000', inputType: 'tel' },
    // SE: eu number has prefix + two additional suffix numbers
    SE_EU: {
      mask: '{SE}0000000000\\01',
      inputType: 'tel',
    },
    SE_VAT: {
      mask: '000000-0000',
      inputType: 'tel',
    },
    GB_VAT: {
      mask: [
        { mask: '{GB}000 0000 00' },
        { mask: '{GB}000 0000 00 000' },
        // TODO: There are two not implemented country codes for UK -> GD and GH, those are for government and health authorities
      ],
      inputType: 'tel',
    },
    other: {
      mask: '**********************',
      inputType: 'text',
    },
  };

  return { ...VAT_FIELD_CONFIG[vatCode], prepare: (value: string) => value.toUpperCase() } || {};
};
